<script setup lang="ts">
// Faixa de ROLLOVER do bônus no header do jogo (Figma 24100:48628). Substitui
// a faixa de XP (InGameProgress.vue) enquanto a sessão roda na carteira de
// bônus — mesma largura cheia, mas uma linha só: sem emblema, sem chips, sem
// segundo degrau, porque não há benefício nenhum pra mostrar aqui.
//
// Valores chegam por prop, com o exemplo do Figma como default — quem passa
// o dado real (usePlayWithBonus().rolloverAmount) é o consumidor
// (components/casino/games/Bar.vue). Quem decide SE esta faixa aparece é o
// `isPlayingWithBonus` do `casino` store, publicado pelo GameIframe.
type Props = {
  amountBet?: number;
  amountTarget?: number;
};

const props = withDefaults(defineProps<Props>(), {
  amountBet: 940,
  amountTarget: 4000,
});

// Cálculo de %/rótulo é o mesmo da versão desktop (InGameRolloverDesktop.vue)
// — só o template/CSS diverge entre as duas, por isso mora num composable.
const { progress, progressLabel, amountLabel } = useRolloverProgress(props);
</script>

<template>
  <!-- O ROOT (quem recebe o `flex-1` de `.levelRow` em Bar.vue) é só um slot:
       fica do tamanho que a games-bar reservar pra faixa e CENTRALIZA a chapa
       dentro dele. Sem esse wrapper, a chapa em si herdava o `flex-1` e o
       fundo escuro esticava pra ocupar a reserva inteira (feita pro desenho
       de duas linhas da faixa de XP) — virava um retângulo alto e feio em vez
       da pilula compacta do Figma. Assim a chapa fica do tamanho do desenho e
       o que sobra vira respiro, não fundo esticado. -->
  <div class="levels-ingame-rollover flex w-full items-center">
    <div
      aria-label="Rollover saldo bônus"
      class="levels-ingame-rollover-plate flex w-full items-center gap-[5px]"
      role="img"
    >
      <span class="label">Rollover saldo bônus</span>

      <span class="bar">
        <span class="track">
          <span
            class="track__fill"
            :style="{ width: `${progress}%` }"
          ></span>

          <!-- Centralizado no TRILHO INTEIRO (não só no preenchido) — é assim
               que o Figma posiciona o texto, pra ele não fugir pra esquerda
               quando o progresso é baixo. -->
          <span class="track__label">{{ amountLabel }}</span>
        </span>

        <span class="percent">{{ progressLabel }}%</span>
      </span>
    </div>
  </div>
</template>

<style scoped lang="scss">
// A faixa inteira mora numa CHAPA (Figma "Union" 24100:48630, #1B1F27) — a
// mesma cor de fundo da placa da faixa de XP (.plate em InGameProgress.vue).
// Altura FIXA (não flex-1): é o wrapper `.levels-ingame-rollover` que estica,
// a chapa fica compacta e centralizada dentro dele.
.levels-ingame-rollover-plate {
  flex: none;
  height: 15.769px;
  padding: 0 3.16px 0 2.59px;
  border-radius: 5.058px;
  background: #1b1f27;
}

.label {
  @apply shrink-0 whitespace-nowrap text-white;

  font-size: 10px;
  font-weight: 600;
  line-height: 1;
}

.bar {
  @apply flex min-w-0 flex-1 items-center;

  gap: 6px;
}

// Mesmo trilho da faixa de XP (.track em InGameProgress.vue), só que a cor da
// borda é fixa (verde-limão a 40%) em vez de vir do elo — o rollover não tem
// elo.
.track {
  @apply relative block min-w-0 flex-1 overflow-hidden;

  height: 12px;
  border-width: 0.632px;
  border-style: solid;
  border-color: rgba(212, 253, 125, 0.4);
  border-radius: 999px;
  background: #001965;
  box-shadow: inset 0 1.153px 2.306px rgba(0, 0, 0, 0.5);
}

.track__fill {
  @apply absolute inset-y-0 left-0 block rounded-full;

  background: linear-gradient(90deg, #34a853, #84cc16);
  box-shadow: 0 0 9.224px rgba(163, 43, 255, 0.9);
}

.track__label {
  @apply absolute inset-0 flex items-center justify-center overflow-hidden whitespace-nowrap px-1 text-white;

  font-size: 6.743px;
  font-weight: 700;
  line-height: 1;
}

.percent {
  @apply shrink-0 text-white tabular-nums;

  font-size: 9.597px;
  font-weight: 700;
  line-height: 1;
}
</style>
